import { LOCUM_CREDENTIALS, MISSING_CREDENTIALS_ERROR_CODE } from '../components/Constants';

const REMINDER_DISMISSED_SESSION_KEY = 'piiReminderDismissed';

/** Whether the once-per-session login reminder was already dismissed (or skipped in onboarding) this session. */
export const wasPiiReminderDismissed = () => {
  try {
    return window.sessionStorage.getItem(REMINDER_DISMISSED_SESSION_KEY) === 'true';
  }
  catch (err) {
    return false;
  }
};

/**
 * Marks the login reminder as handled for this session. Also called when a locum finishes the onboarding
 * numbers step (skipped or not), so they aren't asked again the moment onboarding ends.
 */
export const dismissPiiReminderForSession = () => {
  try {
    window.sessionStorage.setItem(REMINDER_DISMISSED_SESSION_KEY, 'true');
  }
  catch (err) {
    console.error(err);
  }
};

// Wording for each missing credential, phrased to follow "provide", e.g. "Please provide your ABN".
// PII reads as a confirmation so it also fits a locum who answered "No".
const CREDENTIAL_DESCRIPTIONS = {
  [LOCUM_CREDENTIALS.ABN]: 'your ABN',
  [LOCUM_CREDENTIALS.AHPRA]: 'your AHPRA number',
  [LOCUM_CREDENTIALS.PII]: 'confirmation that you hold Professional Indemnity Insurance (PII)',
};

/**
 * Formats missing credentials as a readable list, e.g. "your ABN, your AHPRA number and confirmation that ...".
 * @param {Array<string>} missing - LOCUM_CREDENTIALS values
 */
export const formatMissingCredentials = (missing) => {
  const descriptions = missing.map(credential => CREDENTIAL_DESCRIPTIONS[credential]).filter(Boolean);
  if (descriptions.length <= 1) {
    return descriptions.join('');
  }
  return `${descriptions.slice(0, -1).join(', ')} and ${descriptions[descriptions.length - 1]}`;
};

/**
 * Credentials (LOCUM_CREDENTIALS values) the server says this locum is missing before they can request new shifts.
 * The gate rule lives on the server; the client only reads profile.missingCredentials.
 * @param {Object} profile
 */
export const getMissingCredentials = (profile) => {
  return profile && Array.isArray(profile.missingCredentials) ? profile.missingCredentials : [];
};

/**
 * Returns the `missing` list from a 403 MISSING_CREDENTIALS error thrown by postData, or null for any other error.
 * @param {Error} err
 */
export const getMissingCredentialsFromError = (err) => {
  const response = err ? err.response : null;
  if (err && err.status === 403 && response && response.code === MISSING_CREDENTIALS_ERROR_CODE) {
    return response.missing || [];
  }
  return null;
};
